import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useState, useMemo, useCallback } from 'react'
import { useDispatch } from 'react-redux'

import api from 'api'
import { checkIsWorkOrderLocked } from 'components/billing/common/lock-billing-date/utils'
import WorkOrderActionMenu from 'components/customer-details/tables/WorkOrders/WorkOrderActionMenu'
import { getServiceRecurrenceText } from 'components/locations/LocationsDataGrid/RenderLocationDetailPanelContent/ConfiguredServiceCard'
import DisposalTicketDialog from 'components/work-order/disposal-ticket-dialog/DisposalTicketDialog'
import { isWORedesignFeatureAvailable } from 'data/launch-darkly/featureSelectors'
import { useAccountingLockValue } from 'providers/AccountingLockProvider'
import { combineAddressInfo } from 'utils/helper'

import { getWorkOrderPriceCents, getActiveDisposalTickets, getWorkOrderListInvalidatesTags } from '../utils'

import WorkOrderAccordionItem from './WorkOrderAccordionItem'
import WorkOrderCardItem from './WorkOrderCardItem'

import type { DisposalSite } from '../actions/disposal-ticket/DisposalTicketRow'
import type { WorkOrderListItemApi } from 'types/api/work-order/workOrderList'

type Props = {
  workOrder: WorkOrderListItemApi
  showDivider?: boolean
  showRentalFee?: boolean
  disposalSites?: DisposalSite[]
  onSuccess?: (workOrder?: WorkOrderListItemApi) => void
  showPOChip?: boolean
  showDisposalTicket?: boolean
  useAccordionLayout?: boolean
  leftBlockProps?: Record<string, unknown>
  rightBlockProps?: Record<string, unknown>
  accountId?: string
  cardStyles?: Record<string, unknown>
}

const ignoreSuccess = () => undefined

const WorkOrderListItem: React.FC<Props> = ({
  workOrder,
  showDivider = false,
  showRentalFee = false,
  disposalSites = [],
  onSuccess = ignoreSuccess,
  showPOChip = false,
  showDisposalTicket = false,
  useAccordionLayout = true,
  leftBlockProps = {},
  rightBlockProps = {},
  accountId = '',
  cardStyles = {},
}) => {
  const flags = useFlags()
  const dispatch = useDispatch()
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState<HTMLElement | null>(null)
  const [isOpenDisposalTicketDialog, setIsOpenDisposalTicketDialog] = useState(false)
  const isWORedesignEnabled = isWORedesignFeatureAvailable({ flags })

  const {
    workOrderId = '',
    configuredServiceId,
    locationId,
    pricedServiceType,
    serviceName,
    serviceDate,
    workOrderNumber,
    workOrderStatus,
    completionStatus,
    intentStatus,
    invoiceStatus,
    invoiceDate,
    notesCount = 0,
    purchaseOrderNumber = '',
    feeTotalCents = 0,
    disposalTicketTotalCents = 0,
    exceptions = [],
    routeName = '',
    routeId,
    routeColor,
    servicedAt,
    servicedByFirstName = '',
    servicedByLastName = '',
    serviceActionName = '',
    eventActionType,
    measureUnit = '',
    measureVolume = 0,
    locationName = '',
    sequence = 0,
    rentalFeeCount = 0,
    rentalFeeResetCount = 0,
    disposalTickets = [],
    disposalTicketCount = 0,
    tags,
  } = workOrder

  const { checkIsLocked } = useAccountingLockValue()
  const isLocked = checkIsWorkOrderLocked(invoiceStatus, invoiceDate, checkIsLocked)
  const address = combineAddressInfo(workOrder)
  const recurrenceLabel = useMemo(
    () => getServiceRecurrenceText({ ...workOrder, serviceType: pricedServiceType }, ' '),
    [workOrder, pricedServiceType]
  )
  const totalCents = getWorkOrderPriceCents(workOrder)
  const activeDisposalTickets = getActiveDisposalTickets(disposalTickets)
  const hasDisposalTicket = useMemo(() => disposalTicketCount > 0, [disposalTicketCount])
  const hasPurchaseOrder = useMemo(() => !!purchaseOrderNumber, [purchaseOrderNumber])

  const handleOpenActionMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    event.preventDefault()
    setActionMenuAnchorEl(event.currentTarget)
  }, [])

  const handleCloseActionMenu = useCallback(() => setActionMenuAnchorEl(null), [])

  const handleSuccess = useCallback(() => {
    onSuccess(workOrder)
  }, [onSuccess, workOrder])

  const handleOpenDisposalTicketDialog = useCallback(() => setIsOpenDisposalTicketDialog(true), [])

  const handleCloseDisposalTicketDialog = useCallback(() => setIsOpenDisposalTicketDialog(false), [])

  const handleSuccessDisposalTicket = useCallback(() => {
    const woInvalidateTags = getWorkOrderListInvalidatesTags({
      isNew: isWORedesignEnabled,
      serviceDate,
      woSectionOnly: undefined,
      sectionWise: undefined,
      csId: configuredServiceId,
    })
    dispatch(api.util.invalidateTags([...woInvalidateTags]))
    onSuccess()
  }, [dispatch, isWORedesignEnabled, serviceDate, configuredServiceId, onSuccess])

  const contentProps = {
    workOrderId,
    workOrderNumber,
    configuredServiceId,
    locationId,
    pricedServiceType,
    serviceName,
    serviceDate,
    workOrderStatus: completionStatus || workOrderStatus,
    intentStatus,
    invoiceStatus,
    notesCount,
    purchaseOrderNumber,
    feeTotalCents,
    disposalTicketTotalCents,
    exceptions,
    routeName: routeName ?? undefined,
    routeId: routeId ?? undefined,
    routeColor,
    servicedAt,
    servicedByFirstName,
    servicedByLastName,
    serviceActionName,
    eventActionType,
    measureUnit,
    measureVolume,
    locationName,
    sequence,
    rentalFeeCount,
    rentalFeeResetCount,
    address,
    recurrenceLabel,
    tags,
    totalCents,
    activeDisposalTickets,
    hasDisposalTicket,
    hasPurchaseOrder,
    isLocked,
    showRentalFee,
    showPOChip,
    showDisposalTicket,
    disposalSites,
    leftBlockProps,
    rightBlockProps,
    onSuccess: handleSuccess,
    onOpenDisposalTicketDialog: handleOpenDisposalTicketDialog,
  }

  return (
    <>
      {useAccordionLayout ? (
        <WorkOrderAccordionItem
          accountId={accountId}
          cardStyles={cardStyles}
          contentProps={contentProps}
          showDivider={showDivider}
          workOrder={workOrder}
          onOpenActionMenu={handleOpenActionMenu}
        />
      ) : (
        <WorkOrderCardItem
          cardStyles={cardStyles}
          contentProps={contentProps}
          workOrderId={workOrderId}
          onOpenActionMenu={handleOpenActionMenu}
        />
      )}

      <WorkOrderActionMenu
        enableRoutingActions
        anchorEl={actionMenuAnchorEl}
        workOrder={workOrder}
        onClose={handleCloseActionMenu}
        onSuccess={handleSuccess}
      />
      <DisposalTicketDialog
        configuredServiceId={configuredServiceId ?? ''}
        isLocked={isLocked}
        isOpen={isOpenDisposalTicketDialog}
        serviceDate={serviceDate ?? ''}
        workOrderId={workOrderId}
        onClose={handleCloseDisposalTicketDialog}
        onSuccess={handleSuccessDisposalTicket}
      />
    </>
  )
}

export default WorkOrderListItem
